import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { Card, CardContent } from '../ui/card';
import {
  ScanSmiley,
  Clock,
  Target,
  ChartLineUp,
  CurrencyDollar,
  Sparkle,
  ArrowsHorizontal,
} from '@phosphor-icons/react';

/**
 * Scanner Accuracy Dashboard widget — top of the Timesheet Scanner page.
 *
 * Shows live metrics that turn the scanner from a "tool" into a "value story":
 *   - Total scanned & completion rate
 *   - EVV match accuracy (the unique selling point)
 *   - Hours saved vs manual data entry
 *   - Revenue processed from scanned claims
 */
export const ScannerMetricsWidget = ({ refreshKey }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/timesheets/scanner-metrics');
      setMetrics(res.data);
    } catch (_e) {
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white p-6 mb-6 animate-pulse h-32"
        data-testid="scanner-metrics-loading"
      />
    );
  }
  if (!metrics) return null;

  const { total_scanned, completion_rate, evv_cross_check, time_saved_pretty, revenue_processed, claims_from_scans } = metrics;

  if (total_scanned === 0) {
    // Empty state — not a metric, but a "you'll see metrics here once you start"
    return (
      <Card
        className="mb-6 border-dashed border-2 border-slate-200 bg-gradient-to-br from-indigo-50/30 to-white"
        data-testid="scanner-metrics-empty"
      >
        <CardContent className="pt-6 pb-6 text-center">
          <Sparkle size={28} className="mx-auto text-indigo-500 mb-2" weight="fill" />
          <p className="text-sm text-slate-600">
            Scan your first timesheet to start tracking accuracy metrics, time saved, and revenue processed.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mb-6" data-testid="scanner-metrics-widget">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={<ScanSmiley size={20} weight="fill" className="text-indigo-700" />}
          iconBg="bg-indigo-50"
          label="Timesheets Scanned"
          value={total_scanned.toLocaleString()}
          subtitle={`${completion_rate}% processed`}
          testId="metric-total-scanned"
        />

        <MetricCard
          icon={<Target size={20} weight="fill" className="text-emerald-700" />}
          iconBg="bg-emerald-50"
          label="EVV Match Accuracy"
          value={
            evv_cross_check.total_entries
              ? `${evv_cross_check.exact_match_rate}%`
              : '—'
          }
          subtitle={
            evv_cross_check.total_entries
              ? `${evv_cross_check.exact_matches} of ${evv_cross_check.total_entries} entries`
              : 'No EVV cross-checks yet'
          }
          testId="metric-evv-accuracy"
        />

        <MetricCard
          icon={<Clock size={20} weight="fill" className="text-amber-700" />}
          iconBg="bg-amber-50"
          label="Time Saved"
          value={time_saved_pretty}
          subtitle="vs manual data entry"
          testId="metric-time-saved"
        />

        <MetricCard
          icon={<CurrencyDollar size={20} weight="fill" className="text-purple-700" />}
          iconBg="bg-purple-50"
          label="Revenue Processed"
          value={
            revenue_processed > 0
              ? `$${revenue_processed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : `${claims_from_scans} claims`
          }
          subtitle={
            revenue_processed > 0
              ? `from ${claims_from_scans} scanned claims`
              : 'created from scans'
          }
          testId="metric-revenue"
        />
      </div>

      {/* Accuracy bar — only shows once we have EVV cross-check data */}
      {evv_cross_check.total_entries > 0 && (
        <Card
          className="mt-3 border-slate-200 bg-gradient-to-br from-slate-50 to-white"
          data-testid="metric-accuracy-bar"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ArrowsHorizontal size={16} className="text-indigo-700" />
                <span className="text-sm font-semibold text-slate-700">
                  Timesheet ↔ GPS Reconciliation
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <ChartLineUp size={14} className="text-indigo-600" />
                Avg trust:{' '}
                <span className="font-bold text-indigo-700">
                  {evv_cross_check.avg_trust_score}%
                </span>
              </div>
            </div>

            {/* Stacked bar: exact / minor / major / no_evv */}
            <DistributionBar evv={evv_cross_check} />

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <Legend color="bg-emerald-500" label={`${evv_cross_check.exact_matches} exact`} />
              <Legend color="bg-amber-400" label={`${evv_cross_check.minor_drifts} minor drift`} />
              <Legend color="bg-orange-500" label={`${evv_cross_check.major_drifts} major drift`} />
              <Legend color="bg-slate-300" label={`${evv_cross_check.no_evv_records} new EVV needed`} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MetricCard = ({ icon, iconBg, label, value, subtitle, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg shrink-0`}>{icon}</div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 leading-tight truncate">{label}</p>
          <p className="text-xl font-bold text-slate-900 leading-tight">{value}</p>
          <p className="text-xs text-slate-400 leading-tight truncate">{subtitle}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const DistributionBar = ({ evv }) => {
  const total = evv.total_entries || 1;
  const segments = [
    { id: 'exact', count: evv.exact_matches, color: 'bg-emerald-500' },
    { id: 'minor', count: evv.minor_drifts, color: 'bg-amber-400' },
    { id: 'major', count: evv.major_drifts, color: 'bg-orange-500' },
    { id: 'no_evv', count: evv.no_evv_records, color: 'bg-slate-300' },
  ];
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
      {segments.map((s) =>
        s.count > 0 ? (
          <div
            key={s.id}
            className={s.color}
            style={{ width: `${(s.count / total) * 100}%` }}
          />
        ) : null
      )}
    </div>
  );
};

const Legend = ({ color, label }) => (
  <span className="inline-flex items-center gap-1 text-slate-600">
    <span className={`w-2 h-2 rounded-full ${color}`} />
    {label}
  </span>
);
